<template>
  <v-flex xs12 px-2 pb-4>
    <v-card>
      <v-form :id="emailTemplateFormId" v-model="valid" ref="form" lazy-validation>
        <v-card-title :style="panelColour">
          <span class="truncate">{{editorTemplateName}}</span>
          <v-spacer/>
          <v-icon :color="panelColour.color">mail_outline</v-icon>
        </v-card-title>
        <v-divider/>
        <v-card-text>
          <v-container grid-list-xs>
            <v-layout row wrap>
              <v-flex xs12 sm3>
                <v-text-field :id="templateNameId" :aria-label="templateNameId" v-model="emailTemplateName"
                              :label="$t('admin.alerts.new.email_templates.template_name')"
                              required
                              :rules="requiredRule">
                </v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
          <v-container grid-list-xs>
            <v-layout row wrap>
              <v-flex xs12 sm3>
                <v-text-field :id="templateSubjectId" :aria-label="templateSubjectId" v-model="emailSubject"
                              :label="$t('admin.alerts.new.email_templates.email_subject')"
                              required
                              :rules="requiredRule">
                </v-text-field>
              </v-flex>
              <v-flex xs12 sm3 offset-sm1>
                <combo-box :id="templateRecipientId" :items="translatedRecipients"
                           :parent="`template`"
                           :selectedTemplateRecipients="emailTemplate.emailRecipients" :rule="requiredSelectionRule"
                           @update-this-prop="updateThisProp"/>
              </v-flex>
              <v-flex xs12 sm3 offset-sm1>
                <v-select v-if="!oneDeliveryMethod" :id="deliveryMethodId" :aria-label="deliveryMethodId"
                          :items="deliveryMethodTypes"
                          v-model="emailDeliveryMethods"
                          :label="$t('admin.alerts.new.email_templates.delivery_method_label')"
                          required
                          :rules="requiredSelectionRule">
                </v-select>
              </v-flex>

              <v-flex xs12>
                <span :style="emailBodyLabelStyle">{{$t('admin.alerts.new.email_templates.email_body')}}</span>
                <vue-editor :id="emailBodyId" tabindex="0" v-model="emailBody" :editor-toolbar="customToolbar"/>
                <span v-if="emailBodyValidation" class="caption" style="color: red">{{$t('field_required')}}</span>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-tooltip top>
            <v-btn :id="closeEmailTemplateId" :aria-label="closeEmailTemplateId" slot="activator" icon @click="clickClose">
              <v-icon v-if="emailTemplate.submitted">visibility</v-icon>
              <v-icon v-else>delete</v-icon>
            </v-btn>
            <span>{{closeTooltip}}</span>
          </v-tooltip>
          <v-spacer/>

          <v-tooltip top>
            <v-btn :id="emailPreviewId" :aria-label="emailPreviewId" slot="activator" icon @click="showEmailPreview" :disabled="!hasContent">
              <v-icon v-if="emailPreviewFlag">visibility</v-icon>
              <v-icon v-else>visibility_off</v-icon>
            </v-btn>
            <span>{{emailPreviewTooltip}}</span>
          </v-tooltip>
          <v-tooltip top>
            <v-btn :id="emailTemplateSubmitId" :aria-label="emailTemplateSubmitId" slot="activator" icon @click="clickSubmit">
              <v-icon>check</v-icon>
            </v-btn>
            <span>{{$t('admin.alerts.new.email_templates.submit')}}</span>
          </v-tooltip>
        </v-card-actions>
        <v-divider/>
        <v-card-text v-if="emailPreviewFlag">
          <h3>{{$t('admin.alerts.new.email_templates.email_preview')}}</h3>
          <div class="pa-2 no-line-break">
            <span v-html="emailPreview" ></span>
          </div>
        </v-card-text>
      </v-form>
    </v-card>
  </v-flex>
</template>
<script>
  import {
    SET_TEMPLATE_ITEM_VISIBILITY,
    UPDATE_TEMPLATE_PROP,
    SET_INFORMATION_SNACKBAR_TEXT,
    SET_INFORMATION_SNACKBAR_STATE,
    UNDO_CREATE_NEW_TEMPLATE_ITEM
  } from '../../../store/mutation-types'
  import { mapMutations } from 'vuex'
  import { UPDATE_EMAIL_TEMPLATE_ITEM, POST_NEW_EMAIL_TEMPLATE_ITEM } from '../../../store/action-types'
  import Helpers from '../../../mixins/Helpers'
  import { VueEditor, Quill } from 'vue2-editor'
  import ComboBox from '../../intervention/ComboBox'

  // Register Fonts
  const fontStyle = Quill.import('attributors/style/font')
  fontStyle.whitelist = ['Monospaced', 'Arial']
  Quill.register(fontStyle, true)

  // Register Font-Sizes
  const sizeStyle = Quill.import('attributors/style/size')
  sizeStyle.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '32px', '36px']
  Quill.register(sizeStyle, true)

  export default {
    name: 'alert-template-editor-item',
    mixins: [Helpers],
    components: {ComboBox, VueEditor},
    data () {
      return {
        valid: true,
        requiredRule: [v => !!v || this.$t('field_required')],
        requiredSelectionRule: [v => (!!v && v.length > 0) || this.$t('field_required')],
        emailBodyValidation: false,
        customToolbar: [
          [{'header': [false, 1, 2, 3, 4, 5, 6]}],
          [{'font': [false, 'Monospaced', 'Arial']}],
          [{ 'size': [false, '10px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '32px', '36px'] }],
          ['bold', 'italic', 'underline'],
          [{list: 'ordered'}, {list: 'bullet'}],
          [{'color': []}]
        ],
        recipients: [
          {
            text: 'admin.alerts.new.email_templates.recipients.student',
            value: 'STUDENT'
          },
          {
            text: 'admin.alerts.new.email_templates.recipients.tutor',
            value: 'STAFF'
          }
        ],
        emailPreviewFlag: false,
        editorTemplateName: ''
      }
    },
    props: ['panelColour', 'alertId', 'emailTemplate', 'emailTemplateKey'],
    computed: {
      templateNameId () {
        return `template-name-${this.emailTemplateKey}`
      },
      templateSubjectId () {
        return `template-subject-${this.emailTemplateKey}`
      },
      templateRecipientId () {
        return `template-recipient-${this.emailTemplateKey}`
      },
      deliveryMethodId () {
        return `template-delivery-method-${this.emailTemplateKey}`
      },
      closeEmailTemplateId () {
        return `template-close-${this.emailTemplateKey}`
      },
      emailBodyId () {
        return `email-body-${this.emailTemplateKey}`
      },
      emailTemplateSubmitId () {
        return `template-submit-${this.emailTemplateKey}`
      },
      emailPreviewId () {
        return `preview-email-${this.emailTemplateKey}`
      },
      translatedRecipients () {
        return this.translateFields(this.$store.state.admin.templateRecipientTypes)
      },
      deliveryMethodTypes () {
        return this.$store.state.admin.deliveryMethods
      },
      oneDeliveryMethod () {
        return this.deliveryMethodTypes.length <= 1
      },
      closeTooltip () {
        return this.emailTemplate.submitted ? this.$t('admin.alerts.new.email_templates.hide') : this.$t('admin.alerts.new.email_templates.delete')
      },
      emailPreviewTooltip () {
        return this.emailPreviewFlag ? this.$t('admin.alerts.new.email_templates.hide_email_preview') : this.$t('admin.alerts.new.email_templates.show_email_preview')
      },
      emailBodyLabelStyle () {
        if (this.emailBodyValidation) {
          return {color: 'red'}
        }
      },
      hasContent () {
        return this.emailBody !== null && this.emailBody !== ''
      },
      emailPreview () {
        let placeholderValues = this.$store.state.admin.placeholders
        let emailPreview = this.emailBody
        placeholderValues.forEach(val => {
          let regex = RegExp(val.placeholderIdentifier, 'g')
          if (this.hasContent) {
            emailPreview = emailPreview.replace(regex, val.placeholderDefault)
          }
        })
        return emailPreview
      },
      emailTemplateName: {
        get () {
          this.editorTemplateName = this.emailTemplate.editorTemplateName
          return this.emailTemplate.editorTemplateName
        },
        set (val) {
          if (val) {
            this.editorTemplateName = val
            this.updateThisProp({prop: 'editorTemplateName', val: val})
          }
        }
      },
      emailBody: {
        get () {
          return this.emailTemplate.templateText
        },
        set (val) {
          this.updateThisProp({prop: 'templateText', val: val})
          if (val === '') {
            this.emailPreviewFlag = false
          }
        }
      },
      emailSubject: {
        get () {
          return this.emailTemplate.subjectText
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'subjectText', val: val})
          }
        }
      },
      emailRecipientTypes: {
        get () {
          return this.emailTemplate.emailRecipients
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'emailRecipients', val: val})
          }
        }
      },
      emailDeliveryMethods: {
        get () {
          return this.emailTemplate.deliveryMethod
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'deliveryMethod', val: val})
          }
        }
      },
      emailTemplateFormId () {
        return `template-form-${this.emailTemplateKey}`
      }
    },
    methods: {
      ...mapMutations({
        updateProp: UPDATE_TEMPLATE_PROP,
        setSnackBarText: SET_INFORMATION_SNACKBAR_TEXT,
        setSnackBar: SET_INFORMATION_SNACKBAR_STATE
      }),
      showEmailPreview () {
        this.emailPreviewFlag = !this.emailPreviewFlag
      },
      updateThisProp (prop) {
        this.updateProp({
          key: this.emailTemplateKey,
          prop: prop.prop,
          val: prop.val
        })
      },
      clickClose () {
        if (this.emailTemplate.submitted) {
          this.$store.commit(SET_TEMPLATE_ITEM_VISIBILITY, {key: this.emailTemplateKey, visible: !this.emailTemplate.visible})
        } else {
          this.$store.commit(UNDO_CREATE_NEW_TEMPLATE_ITEM)
        }
      },
      clickSubmit () {
        if (this.$refs.form.validate() && this.hasContent) {
          this.emailBodyValidation = false
          this.updateThisProp({prop: 'submitted', val: true})
          // update descriptions
          this.updateThisProp({prop: 'templateName', val: this.emailTemplate.editorTemplateName})
          this.updateThisProp({prop: 'editorTemplateName', val: this.emailTemplate.editorTemplateName})

          if (this.oneDeliveryMethod) {
            this.updateThisProp({prop: 'deliveryMethod', val: 'EMAIL'})
          }

          // submit
          if (this.emailTemplate.id) {
            this.$store.dispatch(UPDATE_EMAIL_TEMPLATE_ITEM, {
              emailTemplateId: this.emailTemplate.id,
              emailTemplate: this.emailTemplate
            })
          } else {
            this.$store.dispatch(POST_NEW_EMAIL_TEMPLATE_ITEM, {id: this.alertId, emailTemplate: this.emailTemplate})
          }
        } else if (!this.hasContent) {
          this.emailBodyValidation = true
        }
      }
    }
  }
</script>

<style>
  .ql-picker-item[data-value='SansSerif']::before, .ql-picker-label[data-value='SansSerif']::before {
    font-family: SansSerif;
    content: 'Sans Serif' !important;
  }
  .ql-picker-item[data-value='Monospaced']::before, .ql-picker-label[data-value='Monospaced']::before {
    font-family: Monospaced;
    content: 'Monospaced' !important;
  }
  .ql-picker-item[data-value='Arial']::before, .ql-picker-label[data-value='Arial']::before {
    font-family: Arial;
    content: 'Arial' !important;
  }
  .ql-picker-item[data-value='10px']::before, .ql-picker-label[data-value='10px']::before {
    content: '10px' !important;
  }
  .ql-picker-item[data-value='12px']::before, .ql-picker-label[data-value='12px']::before {
    content: '12px' !important;
  }
  .ql-picker-item[data-value='14px']::before, .ql-picker-label[data-value='14px']::before {
    content: '14px' !important;
  }
  .ql-picker-item[data-value='16px']::before, .ql-picker-label[data-value='16px']::before {
    content: '16px' !important;
  }
  .ql-picker-item[data-value='18px']::before, .ql-picker-label[data-value='18px']::before {
    content: '18px' !important;
  }
  .ql-picker-item[data-value='20px']::before, .ql-picker-label[data-value='20px']::before {
    content: '20px' !important;
  }
  .ql-picker-item[data-value='24px']::before, .ql-picker-label[data-value='24px']::before {
    content: '24px' !important;
  }
  .ql-picker-item[data-value='30px']::before, .ql-picker-label[data-value='30px']::before {
    content: '30px' !important;
  }
  .ql-picker-item[data-value='32px']::before, .ql-picker-label[data-value='32px']::before {
    content: '32px' !important;
  }
  .ql-picker-item[data-value='36px']::before, .ql-picker-label[data-value='36px']::before {
    content: '36px' !important;
  }

  .no-line-break p{
    margin-bottom: 0px;
  }

</style>



// WEBPACK FOOTER //
// src/components/settings/alert/AlertTemplateEditorItem.vue